/**
 * Turn the guest who scanned a provider's discharge QR into a patient with an
 * account, keeping the instructions they are looking at.
 *
 * Same shape as upgradeVisitStartGuestToAccount, for the same reason: the
 * anonymous account is upgraded IN PLACE (linkWithCredential), so the uid never
 * changes and the instructions already under `users/{uid}/dischargeInstructions`
 * simply stay where they are. All that is left is telling the server to stop
 * expiring them (keepGuestDischargeInstructions), which clients cannot do
 * themselves because the rules make those docs server-write-only.
 *
 * ORDER MATTERS, as it does there: link, then the user document, then the keep.
 * Once the link succeeds the account exists, so nothing after it may throw except
 * the user doc (without which the patient is bounced between /onboarding and /auth).
 */

import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import firebaseApp, { db } from "services/firebase";
import { buildPatientUserDoc } from "features/visitStart/visitStartAccountUpgrade";
import { keepGuestDischargeInstructions } from "services/healthRecordsShareService";

const friendlyAuthError = (err) => {
  switch (err?.code) {
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return "You already have an account with this email. Please sign in instead, and these instructions will be saved to it.";
    case "auth/weak-password":
      return "Please use a password of at least 6 characters.";
    case "auth/invalid-email":
      return "That does not look like an email address.";
    case "auth/operation-not-allowed":
      return "Email sign-up is not enabled for this app yet.";
    case "auth/requires-recent-login":
      return "This page has been open too long. Please reload it and try again.";
    default:
      return err?.message || "Could not create your account. Please try again.";
  }
};

/**
 * The verification email links back to this discharge page (continueUrl), which
 * Verify.js follows. Without it the link dropped the patient on /onboarding, or
 * on the sign-in page whenever that browser had no session, with no way back.
 * Same fallback as AuthFlow: an unauthorized continue domain still gets a plain email.
 */
const sendVerification = async (user, handoffId) => {
  const settings = handoffId
    ? { url: `${window.location.origin}/discharge?h=${handoffId}`, handleCodeInApp: false }
    : undefined;
  try {
    await user.sendEmailVerification(settings);
  } catch (err) {
    if (
      settings &&
      (err?.code === "auth/unauthorized-continue-uri" || err?.code === "auth/invalid-continue-uri")
    ) {
      await user.sendEmailVerification();
      return;
    }
    throw err;
  }
};

/**
 * @param {{ email: string, password: string, handoffId?: string }} args
 * @returns {Promise<{ uid: string, kept: boolean, verificationSent: boolean }>}
 * @throws {Error} with a message written for the patient.
 */
export async function upgradeDischargeGuestToAccount({ email, password, handoffId } = {}) {
  const cleanEmail = String(email || "").trim();
  if (!cleanEmail || !password) {
    throw new Error("Please enter your email and a password.");
  }

  const current = firebaseApp.auth().currentUser;
  if (!current) {
    throw new Error("This page has expired. Please reload it and try again.");
  }
  if (!current.isAnonymous) {
    throw new Error("You are already signed in.");
  }

  let upgraded;
  try {
    const credential = firebase.auth.EmailAuthProvider.credential(cleanEmail, password);
    const result = await current.linkWithCredential(credential);
    upgraded = result?.user || firebaseApp.auth().currentUser;
  } catch (err) {
    throw new Error(friendlyAuthError(err));
  }

  const uid = upgraded?.uid;
  if (!uid) throw new Error("Could not create your account. Please try again.");

  try {
    await db
      .collection("users")
      .doc(uid)
      .set(buildPatientUserDoc({ uid, email: upgraded.email || cleanEmail }));
  } catch (err) {
    console.warn("[DischargeHandoff] user doc create failed", err);
    throw new Error(
      "Your account was created but we could not finish setting it up. Please sign in to continue.",
    );
  }

  // Best effort, and losing it loses nothing today: guestCleanup only deletes guest
  // copies while reviewing an account that is still anonymous, and this one no longer
  // is. The flags are cleared anyway so no future sweep can mistake them for unowned.
  let kept = false;
  try {
    await keepGuestDischargeInstructions();
    kept = true;
  } catch (err) {
    console.warn("[DischargeHandoff] keep failed", err);
  }

  let verificationSent = false;
  try {
    await sendVerification(upgraded, handoffId);
    verificationSent = true;
  } catch (err) {
    console.warn("[DischargeHandoff] verification email failed", err);
  }

  return { uid, kept, verificationSent };
}
